import { ContentItemBox } from 'ContentItemBox';
import { type LocalePack } from 'ContentItem';
import { type ReactNode, type FC, memo, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import './locales/courseItemBox/courseItemBox-en.json';

type CourseProgressInfo = {
    status: 'completed' | 'inProgress' | 'notStarted' | 'nextUp';
    totalLessons?: number;
    completedLessons?: number;
};

type Props =
    | {
          localePack: LocalePack;
          src: string;
          progressInfo: CourseProgressInfo | null;
          showSubtitle?: boolean;
          onPress: () => void;
      }
    | {
          localePack: LocalePack;
          src: string;
          progressInfo: CourseProgressInfo | null;
          showSubtitle?: boolean;
          expandedContent: ReactNode;
      };

const colorStatusMap = {
    completed: 'text-coral',
    inProgress: 'text-purple',
    notStarted: 'text-slate-grey',
    nextUp: 'text-green',
} as const;

const Icon = memo(({ status }: { status: keyof typeof colorStatusMap }) => (
    <FontAwesomeIcon
        icon={status === 'completed' ? faCheck : faAngleRight}
        // Don't flip checkmarks in rtl
        className={twMerge('text-base', status !== 'completed' ? 'rtl:scale-x-[-1]' : '', colorStatusMap[status])}
    />
));

export const CourseItemBox: FC<Props> = memo(({ localePack, progressInfo, showSubtitle = true, ...rest }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'courseItemBox.courseItemBox' });

    const subtitle = useMemo(() => {
        if (!progressInfo || progressInfo?.status !== 'inProgress' || !showSubtitle) return undefined;

        if (!progressInfo.totalLessons || !progressInfo.completedLessons) return undefined;

        return t('completedLessons', {
            completedLessons: progressInfo.completedLessons,
            totalLessons: progressInfo.totalLessons,
        });
    }, [progressInfo, showSubtitle, t]);

    return (
        <ContentItemBox
            localePack={localePack}
            imageSize={10}
            desktopImageSize={7.5}
            height={18}
            desktopHeight={14.5}
            titleSize="base"
            fullWidth
            endIcon={<Icon status={progressInfo?.status ?? 'notStarted'} />}
            subtitle={subtitle}
            {...rest}
        />
    );
});
